import { createContext, useContext, useEffect, useState } from "react";
import { products as staticProducts, categories as staticCategories } from "../data/siteData";

const ProductsContext = createContext(null);

function slugify(name) {
  return String(name)
    .toLowerCase()
    .replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ı/g, "i")
    .replace(/ö/g, "o").replace(/ş/g, "s").replace(/ü/g, "u")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Not: backend parent_id'yi HAM SAYISAL id olarak döndürür, ama bu
// context'te kategori id'si okunabilir bir slug'a çevriliyor (`/kategori/buketler`
// gibi) — bu yüzden mapZeoCategory tek başına çağrılamaz, üst kategorinin
// slug'ını çözebilmek için tüm satırların numaraFdan-slug'a eşlemesi gerekir
// (bkz. aşağıdaki iki geçişli mapZeoCategories).
// Backend `slug` döndürmezse isimden türetilir — ama iki kategori aynı/benzer
// isme sahipse (ör. iki farklı üst kategori altında birer "Buketler") bu
// çakışan bir slug üretir ve ikinci kategori kendi URL'sinden asla
// erişilemez hale gelir. Ürünlerde olduğu gibi (bkz. mapZeoProduct) ham
// sayısal id sona eklenerek slug'ın HER ZAMAN benzersiz olması sağlanır.
function mapZeoCategory(c, imageBySlug, parentSlug) {
  const slug = c.slug || `${slugify(c.name)}-${c.id}`;
  return {
    id: slug,
    parentId: parentSlug,
    name: c.name,
    // Panelde kategoriye özel bir kapak görseli yüklendiyse onu kullan;
    // yoksa yerleşik statik görsele düşerek boş kutu görünmesini engelle.
    image: c.image_url || imageBySlug[slug] || staticCategories[0]?.image,
  };
}

function mapZeoCategories(rows, imageBySlug) {
  const slugById = {};
  for (const c of rows) {
    slugById[c.id] = c.slug || `${slugify(c.name)}-${c.id}`;
  }
  return rows.map((c) =>
    mapZeoCategory(c, imageBySlug, c.parent_id != null ? slugById[c.parent_id] ?? null : null)
  );
}

function mapZeoProduct(p, catIdToSlug, catIdByName) {
  const hasDiscount = Number(p.discount_percentage) > 0;
  const price = hasDiscount ? Number(p.discounted_price) : Number(p.price);
  const oldPrice = hasDiscount ? Number(p.price) : null;
  const images = Array.isArray(p.images) && p.images.length > 0 ? p.images : [p.image_url].filter(Boolean);

  // Ürün adından okunabilir bir bağlantı (slug) türetilir, ham veritabanı
  // kimliği sonuna eklenir — "/urun/zeo-19" yerine "/urun/gul-buketi-19" gibi
  // daha profesyonel bir link (arka planda hâlâ aynı ürünü işaret eder).
  const nameSlug = slugify(p.name) || "urun";

  return {
    id: `${nameSlug}-${p.id}`,
    name: p.name,
    desc: p.description || "",
    image: p.image_url,
    images,
    price,
    oldPrice,
    rating: null,
    badges: hasDiscount ? ["indirim"] : [],
    // Sayısal category_id (backend'den) üzerinden eşleştirilir — kategori
    // İSMİYLE eşleştirmek, iki kategori aynı isme sahip olduğunda (ör. iki
    // farklı üst kategori altında birer "Buketler") ürünleri yanlış
    // kategoriye düşürüyordu. category_id yoksa (eski backend/proxy cache'i)
    // isme göre eşleştirmeye geri düşülür.
    cat: (p.category_id != null ? catIdToSlug[p.category_id] : null)
      ?? catIdByName[p.category_name]
      ?? slugify(p.category_name || ""),
    // Panelde ürüne eklenen "Gönderim Amacı" etiketleri — Header'daki
    // "Gönderim Amacı" menüsü artık bu diziye göre filtreleme yapıyor
    // (bkz. Shop.jsx ?vesile= parametresi), önceden yalnızca gezinme
    // amaçlı, hiçbir ürünle eşleşmeyen bir bağlantıydı.
    occasionTags: Array.isArray(p.occasion_tags) ? p.occasion_tags : [],
    stock: typeof p.stock === "number" ? p.stock : null,
  };
}

// Fetches live products/categories from the site's own /api/*.php proxies
// (which hold the ZEO Connect API key server-side — never in the browser
// bundle). Starts with the static demo catalog and only switches over once
// the live fetch succeeds, so local dev (where these PHP endpoints don't
// run) never leaves the site looking broken.
//
// Kategoriler ve ürünler BİRBİRİNDEN BAĞIMSIZ olarak canlıya geçer: panelde
// henüz hiç ürün eklenmemiş olsa bile (yeni kiracı / kategoriler daha yeni
// oluşturuldu), panelde tanımlı kategoriler siteye hemen yansır — ürün
// eklenmesini beklemez. Eskiden ikisi de dolu olmadan hiçbiri geçmiyordu.
export function ProductsProvider({ children }) {
  const [products, setProducts] = useState(staticProducts);
  const [categories, setCategories] = useState(staticCategories);
  const [source, setSource] = useState("static");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [catRes, prodRes] = await Promise.all([
          fetch("/api/categories.php"),
          fetch("/api/products.php"),
        ]);

        const imageBySlug = Object.fromEntries(staticCategories.map((c) => [c.id, c.image]));
        let liveCategories = [];
        let catIdToSlug = {};

        if (catRes.ok) {
          const catJson = await catRes.json();
          if (catJson.status !== "error" && Array.isArray(catJson.categories)) {
            liveCategories = mapZeoCategories(catJson.categories, imageBySlug);
            catIdToSlug = Object.fromEntries(
              catJson.categories.map((c) => [c.id, c.slug || `${slugify(c.name)}-${c.id}`])
            );
            if (!cancelled) {
              setCategories(liveCategories);
              setSource("live");
            }
          }
        }

        if (prodRes.ok) {
          const prodJson = await prodRes.json();
          if (prodJson.status !== "error" && Array.isArray(prodJson.products)) {
            // İsimle eşleştirme, category_id olmayan (eski proxy cache'i)
            // ürünler için geriye dönük yedek olarak korunuyor.
            const catIdByName = Object.fromEntries(liveCategories.map((c) => [c.name, c.id]));
            const liveProducts = prodJson.products.map((p) => mapZeoProduct(p, catIdToSlug, catIdByName));
            if (!cancelled) {
              setProducts(liveProducts);
              setSource("live");
            }
          }
        }
      } catch {
        // ZEO Connect'e ulaşılamadı (yerel geliştirme ortamı, ağ hatası vb.)
        // — statik demo katalogla devam edilir, kullanıcıya hata gösterilmez.
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <ProductsContext.Provider value={{ products, categories, source }}>
      {children}
    </ProductsContext.Provider>
  );
}

export function useProducts() {
  const ctx = useContext(ProductsContext);
  if (!ctx) throw new Error("useProducts must be used within a ProductsProvider");
  return ctx;
}
